import {
  Registry,
  RegistryId,
} from '@/react/portainer/registries/types/registry';
import { getURL } from '@/react/portainer/registries/utils/getUrl';

/**
 * The add-on's image is chosen the same way its chart is: a registry, then a path
 * within it. The chart takes one `image.repository` string, so the two are joined
 * on the way out and split apart on the way back in.
 */
/**
 * Registry id sent for an image that needs no credentials. The API reads an
 * omitted field as "keep the registry the add-on is installed with", so going
 * back to a public image has to be said explicitly.
 */
export const PUBLIC_IMAGE_REGISTRY_ID = 0;

export interface AddonImage {
  /** Registry holding the image; undefined uses the repository the chart ships with. */
  registryId?: RegistryId;
  /** Path within the registry, e.g. "portainer/portainer-run". */
  path: string;
}

/**
 * imageRepositoryFor joins the selection into the reference the chart takes, or an
 * empty string to fall back to the chart's own repository.
 *
 * No tag is added: the chart carries its own tag value, and one baked in here
 * would override it.
 */
export function imageRepositoryFor(
  image: AddonImage,
  registries: Array<Registry>
): string {
  const path = image.path.trim();
  if (!path) {
    return '';
  }

  const registry = registries.find(
    (candidate) => candidate.Id === image.registryId
  );

  return registry ? `${getURL(registry)}/${path}` : path;
}

/**
 * splitImageRepository recovers the selection from a stored reference, so the
 * fields can show what the add-on is running. A reference that no configured
 * registry serves keeps its full path and selects no registry — it is then pulled
 * anonymously, as it was before.
 */
export function splitImageRepository(
  repository: string,
  registries: Array<Registry>
): AddonImage {
  if (!repository) {
    return { path: '' };
  }

  const registry = registries.find((candidate) =>
    repository.startsWith(`${getURL(candidate)}/`)
  );

  if (!registry) {
    return { path: repository };
  }

  return {
    registryId: registry.Id,
    path: repository.slice(getURL(registry).length + 1),
  };
}
